<template>
    <div class="page-wrap">
        <div class="page-bar">
            <div>
                <div class="page-bar-sub">Settings</div>
                <div class="page-bar-main">Admin Users</div>
            </div>
            <!-- How admin users, standard users, employees and teams fit together (docs/permissions-guide.html, served from public/), in the PDF popup -->
            <div class="btn-light" @click="showGuide = true">Permissions Guide</div>
        </div>
        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <div v-if="$store.state.user.role == 'Admin'" class="page-filters pf-bar">
                <div class="pf-search">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" /></svg>
                    <input type="text" v-model="searchQ" placeholder="Search" aria-label="Search" @input="search()" />
                </div>
                <span class="pf-spacer"></span>
                <div @click="showAddUserFn()" class="add-btn">Add</div>
            </div>
            <table class="full-table">
                <tbody>
                    <tr class="table-labels">
                        <td>Name</td>
                        <td>Email</td>
                        <td>Role</td>
                        <td></td>
                        <td v-if="$store.state.user.superAdmin"></td>
                        <td></td>
                    </tr>
                    <tr style="padding:0;height:0"></tr>
                    <tr v-for="(user, i) in users" :key="i">
                        <td>{{ user.FirstName }} {{ user.LastName }}<span v-if="!user.EmployeeID" class="no-emp-badge" title="Not linked to a Team Hub employee. Link them from the employee's page (Sign-in accounts).">No employee</span><div v-if="user.EmployeeName" class="user-emp-sub">Employee: {{ user.EmployeeName }}</div></td>
                        <td><a :href="'mailto:'+user.Email" target="_blank">{{ user.Email }}</a></td>
                        <td>{{ user.Role }}</td>
                        <td style="text-align:center">
                            <a @click.prevent="showEditUserFn(user)">Edit</a>
                        </td>
                        <td v-if="$store.state.user.superAdmin" style="text-align:center">
                            <a @click.prevent="signInAsUser(user)">Sign In As</a>
                        </td>
                        <td style="text-align:center">
                            <a @click.prevent="deleteUser(user)">Delete</a>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div v-if="showAddUser" class="error-msg-wrap">
            <div class="form-wrapper slide-up">
                <div class="form-close">
                    <button type="button" class="form-close-x" title="Close" @click="() => { showAddUser = false }">&times;</button>
                </div>
                <h3>Employee</h3>
                <div class="user-emp-hint">Their Team Hub employee record. If that employee also has a standard user, they can switch between Admin and Standard.</div>
                <div v-if="addUserData.employeeID" class="user-emp-picked">{{ employeeName(addUserData.employeeID) }} <a @click.prevent="() => { addUserData.employeeID = null }">Remove</a></div>
                <template v-else>
                    <input type="text" placeholder="Search employees" v-model="employeeSearchQ" />
                    <div class="user-emp-list">
                        <div v-for="emp in employeeOptions(addUserData.userID)" :key="emp.ID">
                            <span v-if="emp.linkedTo" class="user-emp-taken">{{ emp.Name }} · linked to {{ emp.linkedTo }}</span>
                            <a v-else @click.prevent="() => { addUserData.employeeID = emp.ID }">{{ emp.Name }}<span v-if="emp.SeatTitle" class="user-emp-sub"> · {{ emp.SeatTitle }}</span></a>
                        </div>
                    </div>
                    <div class="user-emp-hint">Leave empty to link the employee with their email, if there is one.</div>
                </template>
                <h3>Email</h3>
                <input type="email" v-model="addUserData.email" />
                <h3>First Name</h3>
                <input type="text" v-model="addUserData.firstName" />
                <h3>Last Name</h3>
                <input type="text" v-model="addUserData.lastName" />
                <h3>Phone <span class="item-hint">(optional, for text alerts)</span></h3>
                <phone-input v-model="addUserData.phone" />
                <h3>Content Role</h3>
                <select v-model="addUserData.role">
                    <option value="Editor">Editor</option>
                    <option value="Reviewer">Reviewer</option>
                    <option value="Admin">Admin</option>
                </select>
                <div class="checkbox-input">
                    <input type="checkbox" v-model="addUserData.restrictDepartments" />
                    <h3>Restrict Departments</h3>
                </div>
                <div v-if="addUserData.restrictDepartments">
                    <div style="margin-bottom:20px">Select Departments the user will have access to</div>
                    <div style="margin-bottom: 20px;max-height:120px;overflow:auto">
                        <div v-for="(department, i) in addUserData.departmentsNew" :key="i" class="checkbox-input">
                            <input type="checkbox" v-model="department.isChecked" />
                            <div>{{ department.name }}</div>
                        </div>
                    </div>
                </div>
                <h3>Team Hub Role</h3>
                <select v-model="addUserData.teamHubRole">
                    <option :value="null">User</option>
                    <option value="Admin">Admin</option>
                </select>
                <div v-if="$store.state.user.superAdmin" class="checkbox-input">
                    <input type="checkbox" v-model="addUserData.superAdmin" />
                    <h3>Super Admin</h3>
                </div>
                <input :class="disableButton ? 'disable-button' : ''" type="submit" value="Submit" @click="submitAddUser()" />
            </div>  
        </div>
        <div v-if="showEditUser" class="error-msg-wrap">
            <div class="form-wrapper slide-up">
                <div class="form-close">
                    <button type="button" class="form-close-x" title="Close" @click="() => { showEditUser = false }">&times;</button>
                </div>
                <h3>Employee</h3>
                <div class="user-emp-hint">Their Team Hub employee record. If that employee also has a standard user, they can switch between Admin and Standard.</div>
                <div v-if="editUserData.employeeID" class="user-emp-picked">{{ employeeName(editUserData.employeeID) }} <a @click.prevent="() => { editUserData.employeeID = null }">Remove</a></div>
                <template v-else>
                    <input type="text" placeholder="Search employees" v-model="employeeSearchQ" />
                    <div class="user-emp-list">
                        <div v-for="emp in employeeOptions(editUserData.userID)" :key="emp.ID">
                            <span v-if="emp.linkedTo" class="user-emp-taken">{{ emp.Name }} · linked to {{ emp.linkedTo }}</span>
                            <a v-else @click.prevent="() => { editUserData.employeeID = emp.ID }">{{ emp.Name }}<span v-if="emp.SeatTitle" class="user-emp-sub"> · {{ emp.SeatTitle }}</span></a>
                        </div>
                    </div>
                </template>
                <h3>Email</h3>
                <input type="email" v-model="editUserData.email" />
                <h3>First Name</h3>
                <input type="text" v-model="editUserData.firstName" />
                <h3>Last Name</h3>
                <input type="text" v-model="editUserData.lastName" />
                <h3>Phone <span class="item-hint">(optional, for text alerts)</span></h3>
                <phone-input v-model="editUserData.phone" />
                <h3>Content Role</h3>
                <select v-model="editUserData.role">
                    <option value="Editor">Editor</option>
                    <option value="Reviewer">Reviewer</option>
                    <option value="Admin">Admin</option>
                </select>
                <div class="checkbox-input">
                    <input type="checkbox" v-model="editUserData.restrictDepartments" />
                    <h3>Restrict Departments</h3>
                </div>
                <div v-if="editUserData.restrictDepartments">
                    <div style="margin-bottom:20px">Select Departments the user will have access to</div>
                    <div style="margin-bottom: 20px;max-height:120px;overflow:auto">
                        <div v-for="(department, i) in editUserData.departmentsNew" :key="i" class="checkbox-input">
                            <input type="checkbox" v-model="department.isChecked" />
                            <div>{{ department.name }}</div>
                        </div>
                    </div>
                </div>
                
                <h3>Team Hub Role</h3>
                <select v-model="editUserData.teamHubRole">
                    <option :value="null">User</option>
                    <option value="Admin">Admin</option>
                </select>
                <div v-if="$store.state.user.superAdmin" class="checkbox-input">
                    <!-- Super admins can't remove their own status -->
                    <input type="checkbox" v-model="editUserData.superAdmin" :style="editUserData.userID == $store.state.user.userID ? 'pointer-events:none' : ''" />
                    <h3>Super Admin</h3>
                </div>
                <input :class="disableButton ? 'disable-button' : ''" type="submit" value="Submit" @click="submitEditUser()" />
            </div>  
        </div>
        <confirm-delete v-if="showConfirmDelete" @confirm="deleteUserConfirm()" @cancel="() => { showConfirmDelete = false; }" msg="Are you sure you would like to delete this user?"/>
        <pdf-pop-up v-if="showGuide" url="/ironbook-permissions-guide.pdf" @close="showGuide = false" />
    </div>
</template>

<script lang="ts">
import { defineComponent, registerRuntimeCompiler } from 'vue';
import axios from 'axios';
import ConfirmDelete from '@/components/ConfirmDelete.vue';
import PDFPopUp from '@/components/PDFPopUp.vue';
import PhoneInput from '@/components/PhoneInput.vue';
import { isValidPhone } from '@/phone';

export default defineComponent({
    name: 'UsersView',
    components: {
        ConfirmDelete,
        PdfPopUp: PDFPopUp,
        PhoneInput
    },
    data() {
        return {
            showGuide: false, // the Permissions Guide PDF, from the page header

            allUsers: [] as any,
            users: [] as any,
            employees: [] as any,
            searchQ: '',
            employeeSearchQ: '',
            showConfirmDelete: false,
            userToDelete: null as any,
            addUserData: {
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                role: 'Editor',
                restrictDepartments: false,
                departmentsNew: [] as any,
                departmentsJSON: [] as any,
                teamHubRole: null as any,
                superAdmin: false,
                employeeID: null as any,
                userID: null as any
            },
            showAddUser: false,
            disableButton: false,
            editUserData: {
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                role: '',
                userID: null,
                restrictDepartments: false,
                departmentsNew: [] as any,
                departmentsJSON: [] as any,
                teamHubRole: null as any,
                superAdmin: false,
                employeeID: null as any
            },
            showEditUser: false
        }
    },
    async created() {
        await this.getUsers();

        this.$store.state.routeLoaded = true;
    },
    methods: {
        async getUsers() {
            await axios.get(`${this.$store.state.apiUrl}/api/users`)
                .then(resp => {
                    this.allUsers = resp.data;
                    this.users = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })

            await axios.get(`${this.$store.state.apiUrl}/api/employees/picker`)
                .then(resp => {
                    this.employees = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })
        },
        search() {
            if(!this.searchQ || this.searchQ.length < 3) {
                this.users = this.allUsers;
                return;
            }
            const searchQLower = this.searchQ.toLowerCase();
            this.users = this.allUsers.filter((p: any) => {
                return `${p.FirstName} ${p.LastName}`.toLowerCase().includes(searchQLower)
                    || p.Email.toLowerCase().includes(searchQLower)
            })
        },
        employeeName(id: any): string {
            return this.employees.find((e: any) => e.ID == id)?.Name || 'Employee';
        },
        // Matching employees; ones already linked to another admin show who, and can't be picked
        employeeOptions(userID: any): any[] {
            const q = this.employeeSearchQ.trim().toLowerCase();
            return this.employees
                .filter((e: any) => !q || e.Name.toLowerCase().includes(q) || (e.SeatTitle || '').toLowerCase().includes(q))
                .map((e: any) => {
                    const admin = this.allUsers.find((u: any) => u.EmployeeID == e.ID && u.UserID != userID);
                    return { ...e, linkedTo: admin ? `${admin.FirstName} ${admin.LastName}` : '' };
                });
        },
        closeAddUser() {
            this.searchQ = '';
            this.getUsers();
            this.showAddUser = false;
        },
        async deleteUserConfirm() {
            await axios.delete(`${this.$store.state.apiUrl}/api/users/${this.userToDelete.UserID}`)
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert('There was an error')
                })

            await this.getUsers();

            this.showConfirmDelete = false;
        },
        async deleteUser(user: any) {
            this.userToDelete = user;
            this.showConfirmDelete = true;
        },
        showAddUserFn() {
            this.employeeSearchQ = '';
            this.addUserData = {
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                role: 'Editor',
                restrictDepartments: false,
                departmentsNew: [],
                departmentsJSON: [],
                teamHubRole: null,
                superAdmin: false,
                employeeID: null,
                userID: null
            }
            for(const department of this.$store.state.user.departments) {
                this.addUserData.departmentsNew.push({
                    name: department,
                    isChecked: false
                })
            }
            this.showAddUser = true;
        },
        async submitAddUser() {
            for(const key in this.addUserData) {
                if(key == 'restrictDepartments' || key == 'teamHubRole' || key == 'superAdmin' || key == 'employeeID' || key == 'userID' || key == 'phone') {
                    continue;
                }
                if(!(this.addUserData as any)[key]) {
                    alert('Fill in all fields');
                    return;
                }
            }
            if(this.addUserData.phone && !isValidPhone(this.addUserData.phone)) {
                alert('Enter a complete phone number, or leave it empty');
                return;
            }

            this.addUserData.departmentsJSON = [];
            if(this.addUserData.restrictDepartments) {
                for(const department of this.addUserData.departmentsNew) {
                    if(department.isChecked) {
                        this.addUserData.departmentsJSON.push(department.name);
                    }
                }
                if(!this.addUserData.departmentsJSON.length) {
                    alert('Give user access to at least one department');
                    return;
                }
            }

            this.disableButton = true;

            await axios.post(`${this.$store.state.apiUrl}/api/users`, this.addUserData)
                .then(resp => {
                    if(resp.data?.warning) {
                        alert("The user was added, but their employee wasn't linked: " + resp.data.warning);
                    }
                    this.getUsers();
                    this.showAddUser = false;
                })
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert('There was an error')
                })

            this.disableButton = false;
        },
        showEditUserFn(user: any) {
            this.employeeSearchQ = '';
            this.editUserData = {
                email: user.Email,
                firstName: user.FirstName,
                lastName: user.LastName,
                phone: user.Phone || '',
                userID: user.UserID,
                role: user.Role,
                restrictDepartments: user.RestrictDepartments,
                departmentsNew: [],
                departmentsJSON: [],
                teamHubRole: user.TeamHubRole,
                superAdmin: !!user.SuperAdmin,
                employeeID: user.EmployeeID || null
            }
            const currentDepartments = JSON.parse(user.DepartmentsJSON);
            for(const department of this.$store.state.user.departments) {
                let isChecked = currentDepartments.includes(department);
                this.editUserData.departmentsNew.push({
                    name: department,
                    isChecked: isChecked
                })
            }
            this.showEditUser = true;
        },
        async submitEditUser() {
            for(const key in this.editUserData) {
                if(key == 'restrictDepartments' || key == 'teamHubRole' || key == 'superAdmin' || key == 'employeeID' || key == 'phone') {
                    continue;
                }
                if(!(this.editUserData as any)[key]) {
                    alert('Fill in all fields');
                    return;
                }
            }
            if(this.editUserData.phone && !isValidPhone(this.editUserData.phone)) {
                alert('Enter a complete phone number, or leave it empty');
                return;
            }

            this.editUserData.departmentsJSON = [];
            if(this.editUserData.restrictDepartments) {
                for(const department of this.editUserData.departmentsNew) {
                    if(department.isChecked) {
                        this.editUserData.departmentsJSON.push(department.name);
                    }
                }
                if(!this.editUserData.departmentsJSON.length) {
                    alert('Give user access to at least one department');
                    return;
                }
            }

            this.disableButton = true;

            await axios.post(`${this.$store.state.apiUrl}/api/users/edit`, this.editUserData)
                .then(() => {
                    this.getUsers();
                    this.showEditUser = false;
                })
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert(e?.response?.data || 'There was an error')
                })

            this.disableButton = false;
        },
        signInAsUser(user: any) {
            axios.get(`${this.$store.state.apiUrl}/api/signinasotheradmin?userID=${user.UserID}`)
                .then(resp => {
                    window.location.href = '/';
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error')
                })
        },
    }
});
</script>

<style scoped>
.user-emp-hint { font-size: 12.5px; color: #a6a29d; margin: -8px 0 10px; }
.user-emp-picked { margin-bottom: 20px; font-weight: 600; }
.user-emp-picked a { margin-left: 10px; font-weight: normal; text-decoration: underline; cursor: pointer; }
.user-emp-list { margin-bottom: 20px; max-height: 160px; overflow: auto; }
.user-emp-list > div { margin-bottom: 5px; }
.user-emp-list a { cursor: pointer; }
.user-emp-taken { color: #a6a29d; }
</style>
